/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2014, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * JQuery datatables grid control (JQGrid).
 * @module com/ibm/ui/render/grid/control/JQGrid
 */
define([
	'underscore',
	'jquery',
	'../../../../util/DashboardFormatter',
	'../../../../widgets/livewidget/nls/StringResources',
	'../GridDataProcessor',
	'../GridDropTarget',
	'../GridDnDManager',
	'../../../../dataSources/metadata/MetadataColumn',
	'../RankProcessor',
	'../GridDataProvider',
	'../GridContentRenderer',
	'../GridNullSuppressionProcessor',
	'react-dom',
	'react',
	'@ba/ba-ui-carbon-toolkit'
], (_, $, Formatter, StringResources, GridDataProcessor, GridDropTarget, GridDnDManager, MetadataColumn, RankProcessor, DataProvider, GridContentRenderer, GridNullSuppressionProcessor, ReactDOM, React, Toolkit) => {

	const SLOT_ID = 'grid_cols';
	const SUMMARY_COLS_THRESHOLD = 12;
	const ROW_HEADER_INDEX = 0;
	const LABEL = 'label';
	const VALUE = 'value';
	const DEFAULT_ROW_HEIGHT = 32;
	const PROP_ID_SUPPRESSION = 'suppression';

	const SUMMARY_ID = StringResources.get('summaryCaption');

	return class JQGrid {

		constructor(...args) {
			this.init(args[0]);
		}

		init(options = {}) {
			this.domNode = options.domNode;
			this.gridView = options.gridView;
			this.colorsService = options.dashboardApi.getFeature('Colors');
			this.iconsFeature = options.dashboardApi.getFeature('Icons');
			this.tabNavigation = options.tabNavigation;
			this.conditionalFormatting = options.conditionalFormatting;
			this.cellRenderer = options.cellRenderer;

			this._dataRows = null; //Data used by the grid in row form.

			this._heatByIndex = -1;
			this._heatIndice = [];
			this._colorAllColumns = false;
			this._bGroupingEnabled = true;
			this._filterableSlots = [];
			this._selectedCellCache = {}; // Selected data cache
			this._selectedHeaderColumnCache = {}; // Selected header column cache
			this._slotFormatCache = {};

			this._rankDataItems = [];
			this._columnDataItems = [];
			this._visModel = options.visModel;

			this._hideSummaries = false;
			this._hasTopBottomLimit = null;

			this._columnsInfo = {
				groupedIndices: [],
				factIndices: []
			};

			const content = this.gridView && this.gridView.content;

			this.summaryFeature = options.summaryFeature;
			this.visualization = content.getFeature('Visualization');
			this._gridDnDManager = new GridDnDManager({
				grid: this,
				dashboardApi: options.dashboardApi,
				content: content,
				gridEdgeMappedToId: { row: SLOT_ID }
			});

			this._setSummaryColor = (gridCellProps) => {
				const summaryColor = this.conditionalFormatting && this.conditionalFormatting.getSummaryFillColor();
				if (summaryColor) {
					_.extend(gridCellProps.style, { 'background-color': summaryColor });
				}
			};
			this.renderedTheme = this.colorsService.getThemeDefinition().getId();
		}

		/**
		 * Resets the table - should be called before the first call to setData.
		 * @param {object} visModel
		 */
		reset(visModel) {
			this._visModel = visModel;
			this._heatIndice = [];
			this._heatByIndex = -1;
			this._colorAllColumns = false;
			this._columnSlot = this.visualization.getSlots().getSlot('grid_cols');
			this._heatSlot = this.visualization.getSlots().getSlot('heat');
			this._slots = [this._columnSlot, this._heatSlot];
			this._rankDataItems = [];
			this._columnDataItems = [];
			this._dataRows = [];
			this._hasTopBottomLimit = null;
			this._columnsInfo = {
				groupedIndices: [],
				factIndices: []
			};
		}

		// These configurations need the rank to be processed before being set
		_whenRankProcessed() {
			this._queryResult.processNullSuppression();
			this._columnDataItems = this._rankDataItems.concat((this._queryResult.getSlotDataItemList(this._columnSlot)) || []);
			this._filterHeatBySlot(this.visualization.getSlots().getSlotList());
			this._setColumnsInfo();
			this._doGrouping = this._columnsInfo && !(this._columnsInfo.disableGrouping || this._hasTopBottomLimit);
			this._setDoSummaryInfo();
		}

		_setDoSummaryInfo() {
			this._hideSummaries = this._visModel.getPropertyValue('hideSummaries');

			if (this._hideSummaries === undefined && ((this._columnDataItems ? this._columnDataItems.length : 0) > SUMMARY_COLS_THRESHOLD)) {
				// only override the default of hideSummaries on the first time Through
				// sanity check for defect 247618 to mitigate risk of crash on initial rendering of very large tables
				// user can change at own risk after first render
				this._hideSummaries = true;
				this.gridView.ownerWidget.updateVisProperties({
					id: 'hideSummaries',
					value: true
				}, {
					silent: true
				});
			}

			const hasFactInColumn = this._columnsInfo.factIndices.length;
			const hasGroup = this._columnsInfo.groupedIndices.length;
			this._doSummary = (hasFactInColumn && hasGroup) && !this._hideSummaries && this._doGrouping;
		}

		_setColumnsInfo() {
			if (this._columnDataItems && this._columnDataItems.length) {
				const numberOfColumns = this._columnDataItems.length;
				const groupedIndices = [];
				const factIndices = [];
				this._columnsInfo = {};

				this._setLeftMostFactSlotIndex();

				// const bPreviousColumnHasUserSort = true;
				// const bCheckForDisableGrouping = true;
				this._columnsInfo.disableGrouping = false;

				for (let columnIndex = 0; columnIndex < numberOfColumns; columnIndex++) {
					const dataItem = this._columnDataItems[columnIndex];

					//TODO: Add Top-N support here
					// const mapping = slot.getMapping();
					// if (!this._hasTopBottomLimit) {
					// 	const limit = mapping.getLimit ? mapping.getLimit() : undefined;
					// 	this._hasTopBottomLimit = (limit !== undefined);
					// }

					//TODO: Rewrite this logic to use new model
					if (this._columnCanBeGrouped(columnIndex)) {
						groupedIndices.push(columnIndex);
					}

					if (this._isFactOrAggregationSet(dataItem) || this.isRank(columnIndex)) {
						factIndices.push(columnIndex);
					}
				}

				this._columnsInfo.factIndices = factIndices;
				this._columnsInfo.groupedIndices = groupedIndices;
			}

		}

		_setLeftMostFactSlotIndex() {
			delete this._columnsInfo.leftMostFactSlotIndex;
			for (let i = 0; i < this._columnDataItems.length; i++) {
				if (this._isFactOrAggregationSet(this._columnDataItems[i]) || this.isRank(i)) {
					this._columnsInfo.leftMostFactSlotIndex = i;
					break;
				}
			}
		}

		/**
		 * A slot can be grouped (i.e. only one value displayed if there is muliple )
		 * IF it is an attribute OR it has an attribute concept (like cAge, cWeek, cDate) attatched to it OR slot definition indicates group = true.
		 * Otherwise, it should not be grouped (as in the case of measures like Quantity or Units Sold). See RTC 73968 for details.
		 * @param colIndex - index of the column in question
		 * @returns true if the slot should be grouped...false if each value should be rendered even if it is the same as the previous row.
		 *
		 */
		_columnCanBeGrouped(colIndex) {
			const leftMostCol = this._columnsInfo.leftMostFactSlotIndex;
			if (!_.isUndefined(leftMostCol) && colIndex >= leftMostCol) {
				return false;
			}

			return this._columnDataItems[colIndex].getType() === 'attribute';
			//TODO: Put in equivalent for this condition:
			//|| (slot.getDefinition() && slot.getDefinition().group)
		}


		_filterHeatBySlot(dataSlots) {
			if (!this._hasDataMappings()) {
				return;
			}
			let index = this.getRankDataItemCount();
			const heatDataItemList = this._heatSlot.getDataItemList();
			const heatByColumnId = (this._heatSlot && heatDataItemList.length > 0) ? heatDataItemList[0].getColumnId() : null;
			//return an array containing all of the non-heat slots and
			//locate what column index in the data that the heat slot will be located
			const newSlots = _.filter(dataSlots, (slot) => {
				const dataItemList = this._queryResult.getSlotDataItemList(slot);
				if (dataItemList.length == 0) {
					return false;
				}

				if (slot.getId() === 'heat') {
					this._heatByIndex = index++;
					// ignore heat slot
					return false;
				} else {
					index += dataItemList.length;
					return true;
				}

			});

			// Collect the slots indice that matches the heatby column id
			if (heatByColumnId) {
				index = this.getRankDataItemCount();
				_.each(newSlots, (slot) => {
					const dataItemList = this._queryResult.getSlotDataItemList(slot);
					//slots now contain more than 1 data item so have to iterate over the data items inside
					if (dataItemList.length > 0) {
						const itemIds = _.map(dataItemList, (dataItem) => { return dataItem.getColumnId(); });
						_.each(itemIds, (dataItemId) => {
							if (dataItemId === heatByColumnId) {
								this._heatIndice.push(index);
							}
							index++;
						});
					}
				});

				// format the entire row. -1 in the array indicates that heat slot
				// is populated but the column is not displayed in the grid
				if (this._heatIndice.length === 0) {
					this._colorAllColumns = true;
				}
			}
			return newSlots;
		}

		setData(queryResult) {
			// @todo this must be revisited for V2 Query
			this._queryResult = new GridNullSuppressionProcessor( {
				columnSlot: this._columnSlot,
				heatSlot: this._heatSlot,
				queryResult: queryResult,
				isSuppressionOn: this._isSuppressionOn()
			});
			this._processRank();
		}

		hasData() {
			return !!(this._queryResult);
		}

		setStyles(styleObj) {
			if (styleObj) {
				this.headerStyles = styleObj.headerStyles;
				this.cellStyles = styleObj.cellStyles;
			}
		}

		resetOnRender() {
			if (this._groupedConditionalColor) {
				delete this._groupedConditionalColor;
			}

			this._setDoSummaryInfo();
		}

		// Modifies original data, should not be called more than once per data set
		_processRank() {
			const rankProcessor = new RankProcessor(this._queryResult, this.findSlotDetailsByRealColumnIndex.bind(this));
			this._rankDataItems = rankProcessor.processData();
			this._whenRankProcessed();
		}

		_generateDataRows() {
			// rank processor is only called once upon setData for new query result
			// client side format change will no longer trigger a new query, but the model change will trigger a reset on visModel
			// need to make sure that the columnInfo is set in this scenario
			if (this._columnDataItems.length === 0) {
				this._whenRankProcessed();
			}
			this._resetOnGetDataRows();
			const dataProcessor = new GridDataProcessor({
				'queryResult': this._queryResult,
				'numberOfColumns': this._columnDataItems ? this._columnDataItems.length : 0,
				'exceedsColumnThreshold': ((this._columnDataItems ? this._columnDataItems.length : 0) > SUMMARY_COLS_THRESHOLD),
				'columnSlot': this._columnSlot,
				'rankDataItems': this._rankDataItems,
				'columnDataItems': this._columnDataItems,
				'lastGroupedColumnIndex': this._columnsInfo ? this._columnsInfo.leftMostFactSlotIndex : 0,
				'visModel': this._visModel,
				'factColumnIndices': this._columnsInfo ? this._columnsInfo.factIndices : 0,
				'doSummary': this._doSummary,
				'onCellDataChange': this._onCellDataChange.bind(this),
				'getRankFormat': this._getRankFormat,
				'getRealColumnIndex': this.getRealColumnIndex.bind(this),
				'summaryFeature': this.summaryFeature
			});

			return dataProcessor.getData().then(dataRows => {
				this._dataRows = dataRows;
				if (this._columnDataItems.length > 0) {
					this._headers = this._columnDataItems.map((dataItem, index) => {
						let label = dataItem.getLabel();
						if (this.isRank(index)) {
							label = StringResources.get('topBottomRankLabel', {
								label: label
							});
						}
						return label;
					});
				}
				this._dataRows.unshift(this._headers);
				this._WhenDataRowsAreReady();
				//TODO: clean-up is to remove the unnecessary creation of summary value promises;
				// this can definitely be done when we have only one request but also without this: as discussed, we could wait for the summary promises to be resolved only
				dataProcessor.whenAllSummaryValuesAreReady().then(this.onResize.bind(this));
			});
		}

		onResize() {
			this.maxColHeight = this._getMaxColHeight();
			this.gridRef && this.gridRef.recomputeGridSize();
		}

		render(resizing, renderingNewData) {
			const currentDashboardTheme = this.colorsService.getThemeDefinition().getId();
			if (this.gridRef && resizing && !renderingNewData && this.renderedTheme === currentDashboardTheme) {
				//if only do resizing
				this.onResize();
				this.onSyncScrollBar();
				return Promise.resolve(true);
			} else {
				this.renderedTheme = currentDashboardTheme;
				// dataChange will change the rowNum and colNum of the data, need to re-render the grid
				//reset summary indicator flag
				if (!this._errorWarningAdded) {
					this.gridView && this.gridView.clearInfoIndicator('error_summary_cell');
				} else {
					this._errorWarningAdded = false;
				}
				this.resetOnRender();
				return this._generateDataRows()
					.then(this._renderReactGrid.bind(this))
					.then(() => !!this.gridRef);
			}
		}

		onSyncScrollBar() {
			// when parent dom is moved or updated the scrollLeft and scrollTop value reset to 0 for the bottom-right-grid
			// this is a bug in React MultiGrid Level
			// the fix here is to sync the scrollBar among the three grid before onResize is triggered
			const $domNode = $(this.domNode);
			const rightBottomGrid = $domNode.find('.ba-common-grid__bottom-right-grid');
			const rightTopGrid = $domNode.find('.ba-common-grid__top-right-grid');
			const visibleChildrenCells = $(rightBottomGrid).find('.bottom-right-grid-cell');
			const firstVisibleChild = visibleChildrenCells && visibleChildrenCells[0];
			$(rightBottomGrid).animate({
				scrollLeft: $(rightTopGrid).scrollLeft(),
				scrollTop: firstVisibleChild && firstVisibleChild.offsetTop || 1
			}, 0);
		}

		/**
		 * Set selections context which contains row edge selection, column edge selection
		 * and datapoint selection
		 **/
		setSelections(selections) {
			this._selections = selections;
			this._selectionValues = [];
			this._selectedCellCache = {};
			this._selections.forEach(selection => {
				let _tempSelection = [];
				Object.values(selection).forEach(s => {
					_tempSelection.push(s.u);
				});
				this._selectionValues.push(_tempSelection);
			});
		}

		_getDataCellValue(dataCellValue, valueType) {
			if (dataCellValue === null || dataCellValue === undefined) {
				return;
			}
			if (valueType === 'useValue') {
				dataCellValue = this._getValue(dataCellValue, VALUE);
			} else if (valueType === 'type') {
				dataCellValue = dataCellValue.type;
			} else if (valueType === 'isSummary') {
				dataCellValue = dataCellValue.isSummary;
			} else if (valueType === 'summaryOf') {
				dataCellValue = dataCellValue.summaryOf;
			} else if (valueType === 'tuple') {
				dataCellValue = dataCellValue.key;
			} else if (valueType === 'rank') {
				dataCellValue = dataCellValue.value && (dataCellValue.value.deco && dataCellValue.value.deco.rank || dataCellValue.value.value) || dataCellValue.value;
			} else {
				if (dataCellValue.isSummary && this._getValue(dataCellValue, VALUE) === dataCellValue.summaryOf) {
					dataCellValue = SUMMARY_ID;
				} else {
					dataCellValue = this._getValue(dataCellValue, LABEL);
				}
			}
			return dataCellValue;
		}

		_getValueForRowAtIndex(row, colIndex) {
			const cell = row[colIndex];
			if ( cell.value && cell.value.value ) {
				return cell.value.value;
			} else if ( cell[0] ) {
				return cell[0].value;
			} else {
				return null;
			}
		}

		_calculateSelectionForFact(rowIndex) {
			const row = this._dataRows[rowIndex + 1];
			let isSelected = false;
			let attributeValues = [];
			for (let i = 0; i < row.length; i++) {
				if (!this._columnsInfo.factIndices.includes(i)) {
					const cellValue = this._getValueForRowAtIndex(row, i);
					attributeValues.push(cellValue);
				}
			}
			this._selectionValues.forEach(selection => {
				if (_.difference(selection, attributeValues).length === 0) {
					isSelected = true;
				}
			});
			return isSelected;
		}

		/**
		 * Determine if a cell should be selected based on the selection info
		 *
		 * @param {object} dataValue
		 * @param {number} colIndex
		 * @return {boolean}
		 * @private
		 */
		_isCellSelected(rowIndex, colIndex) {
			const cacheKey = this._getCellCacheKey(rowIndex, colIndex);
			if (this._selectedCellCache[cacheKey] !== undefined) {
				return this._selectedCellCache[cacheKey];
			}

			let isSelected = false;
			if ( this._selectionValues.length > 0 ) {
				if (this._columnsInfo.factIndices.includes(colIndex)) {
					isSelected = this._calculateSelectionForFact(rowIndex);
				} else {
					const row = this._dataRows[rowIndex + 1];
					const cellValue = row && this._getValueForRowAtIndex(row, colIndex);
					isSelected = _.flatten(this._selectionValues).includes(cellValue);
				}
			}
			this._selectedCellCache[cacheKey] = isSelected;
			return isSelected;
		}

		_getCellCacheKey (rowIndex, colIndex) {
			return `${rowIndex}-${colIndex}`;
		}

		_getCellPropertyColors() {
			try {
				// if user customized a background color for current widget
				const fillColor = this.gridView && this.gridView.content && this.gridView.content.getPropertyValue('fillColor');
				const cellFillColor = fillColor && this.colorsService.getHexColorFromDashboardColorSet(fillColor);
				// TODO: have our own theme mapping for new grid
				const foregroundColorProperties = fillColor && this.colorsService.getForegroundColorPropertiesForUIElement(fillColor, 'tabs');
				const cellTextColor = foregroundColorProperties &&  foregroundColorProperties.length && foregroundColorProperties[0].value;
				return {
					cellFillColor,
					cellTextColor
				};
			} catch ( err ) {
				return {};
			}
		}

		_prepFormatCache(numColumns) {
			this._slotFormatCache = {};
			for ( let i = 0; i < numColumns; i++ ) {
				const { slot, mapIndex } = this.findSlotDetails(i);
				this._slotFormatCache[i] = this._queryResult.getSlotDataItemList(slot)[mapIndex].getFormat(mapIndex);   // this call is expensive!
			}
		}

		_renderReactGrid() {
			const _dataProvider = new DataProvider(this._dataRows);
			const fixedRows = 1;
			const numColumns = _dataProvider.getNumColumns();
			const numRows = _dataProvider.getNumRows() - fixedRows;
			const dataRows = _dataProvider.getNumRows() - (_dataProvider.getNumRows() - this._queryResult.getRowCount());

			this._prepFormatCache(numColumns);

			// in story, the first-time rendering from dashboard, the offsetHeight of the node is 0, need to check this
			this.maxColHeight = this._getMaxColHeight();
			const TABINDEX = 0;
			const summaryRowIdxs = [];
			const isSuppressed = this._isSuppressionOn();
			const { cellFillColor, cellTextColor } = this._getCellPropertyColors();
			const _shapeCols = [];

			// find heat dataItem position so it is associated with its fact column
			let heatColumnPosition = this._determineHeatColumnPosition();

			let coveredCellSet = new Set();

			const dataCellRenderer = (rowIndex, colIndex, gridCellProps) => {
				try {
					const currentRow = rowIndex + fixedRows;
					const currentCol = colIndex;

					if (coveredCellSet.has(`${currentRow} ${currentCol}`)){
						return null;
					}

					const dataValue = _dataProvider.getValue(currentRow, currentCol);
					const isSummary = this._getDataCellValue(dataValue, 'isSummary');
					const isRank = this.isRank(colIndex);
					const isMeasure = colIndex < this._columnDataItems.length && this._isFactOrAggregationSet(this._columnDataItems[colIndex]);
					//Summary values are already formatted so only format non-summary values
					const rawDataCellValue = this._getDataCellValue(dataValue);
					const dataCellValue = isSummary ? rawDataCellValue : (isRank ? this._getDataCellValue(dataValue, 'rank') : this._formatValue(rawDataCellValue, colIndex, isSummary, isSuppressed));

					//populate the attributes we needed
					gridCellProps['row'] = rowIndex + fixedRows;
					gridCellProps['col'] = colIndex;
					gridCellProps['aria-label'] = dataCellValue;
					gridCellProps['tabindex'] = TABINDEX;

					const classList = [gridCellProps.className];
					classList.push('grid-data-cell', 'dashboard-grid-cell', 'table-view');
					gridCellProps.style = _.extend(gridCellProps.style, {});

					const isSelected = this._isCellSelected(rowIndex, colIndex);

					// background color from Property
					if (cellFillColor && !isSummary){
						gridCellProps.style = _.extend(gridCellProps.style, {
							color: cellTextColor
						});
						if (!isSelected) {
							gridCellProps.style.backgroundColor = cellFillColor;
						}
					}

					//Text toolbar style applied
					const values = Object.assign({},this.cellStyles);
					Object.keys(values).forEach((key) => values[key] === '' && delete values[key]);
					gridCellProps.style = Object.assign(gridCellProps.style, values);

					//summary row background
					let summaryCnt = 0;
					if (isSummary) {
						classList.push('summary', 'summary-row-top-border');
						const found = summaryRowIdxs.find((ele) => {
							return rowIndex === ele;
						});
						if (!found) {
							summaryRowIdxs.push(rowIndex);
						}
					}
					// determine summary row cnt for conditional formatting
					summaryRowIdxs.forEach((idx) => {
						if (rowIndex > idx) {
							summaryCnt++;
						}
					});

					//Conditional Formatting
					if (!isSummary && rowIndex < dataRows + summaryCnt && !dataValue.hasLeafNode) {
						const conditionalColor = this._getConditionalColor(rowIndex - summaryCnt, colIndex, dataValue, heatColumnPosition);
						if (conditionalColor) {
							if (!this.conditionalFormatting) {
								_.extend(gridCellProps.style, conditionalColor.getStyle());
							} else {
								_.extend(gridCellProps.style, conditionalColor);
								// Remove conditional background colour since theme-able background colour take precedence when the cell is selected
								if (isSelected) {
									delete gridCellProps.style['backgroundColor'];
								}
							}
						}
					}

					// colspan/rowspan
					let rowSpan = 0;
					let colSpan = 0;
					if (this.isGroupedColumn(colIndex) && !isSummary) {
						let rowSpanIndex = rowIndex + fixedRows + 1;
						//iterate over the rows in this row until we find one that is different
						while (rowSpanIndex <= numRows + fixedRows - 1
							&& this._getDataCellValue(_dataProvider.getValue(rowSpanIndex, colIndex)) === rawDataCellValue
							&& this._getDataCellValue(_dataProvider.getValue(rowSpanIndex, 0)) === this._getDataCellValue(_dataProvider.getValue(rowIndex + fixedRows, 0))) {
							rowSpanIndex++;
						}
						rowSpan = rowSpanIndex - (rowIndex + fixedRows);
						const _height = parseInt(gridCellProps.style.height, 10) * rowSpan;
						if(rowSpan > 1 && rowSpan + currentRow >= _dataProvider.getNumRows()){
							gridCellProps.style.height = Math.min(_height, this.maxColHeight) - 2 + 'px';
						}else{
							gridCellProps.style.height = Math.min(_height, this.maxColHeight) + 'px';
						}
					}

					let isCellBelowInSummaryRow;
					if (isMeasure) {
						isCellBelowInSummaryRow = this._isCellBelowInSummaryRow(currentRow + 1, currentCol, _dataProvider);
					} else {
						const nextRow = rowSpan > 0 ? currentRow + rowSpan : currentRow + 1;
						isCellBelowInSummaryRow = this._isCellBelowInSummaryRow(nextRow, currentCol, _dataProvider);
					}
					if (isSummary && !isCellBelowInSummaryRow) {
						classList.push('summary-row-bottom-border');
					}
					if (!isMeasure && !isSummary && isCellBelowInSummaryRow) {
						gridCellProps.style.height = parseFloat(gridCellProps.style.height) - 2 + 'px';
					}

					if (isSummary && !isMeasure) {
						let colSpanIndex = colIndex + 1;
						while (colSpanIndex <= numColumns + 1 && this._getDataCellValue(_dataProvider.getValue(rowIndex + fixedRows, colSpanIndex)) === dataCellValue) {
							colSpanIndex++;
						}
						colSpan = colSpanIndex - colIndex;
						gridCellProps.style.width = parseInt(gridCellProps.style.width, 10) * colSpan;
						gridCellProps.style['font-weight'] = 'bold';
						if (colSpan && !rowSpan) {
							rowSpan = 1;
							colSpan--;
						}
					}

					if (rowSpan || colSpan) {
						for (let row = 0; row <= rowSpan - 1; row++){
							for (let col = 0; col <= colSpan; col++){
								if (row !== 0 || col !== 0){
									coveredCellSet.add(`${currentRow + row} ${currentCol + col}`);
								}
							}
						}
					}

					//render selection highlight and implied_selection for fact column
					if (!isSummary && isSelected) {
						classList.push('dashboard-grid-cell-selected');
					}

					if (isMeasure) {
						classList.push('measure');
					} else {
						classList.push('category');
					}

					gridCellProps.className = classList.join(' ');

					const contentSpec = {
						displayValue: dataCellValue,
						iconsFeature: this.iconsFeature
					};

					let isShapeCol = _shapeCols.includes(colIndex);
					if (isShapeCol || gridCellProps.style.shapeId) {
						if (!isShapeCol) {
							_shapeCols.push(colIndex);
						}
						contentSpec.shapeId = gridCellProps.style.shapeId;
					}

					let children;
					if (this.cellRenderer && this.cellRenderer.canRender()){
						// This code is currently not used. It is only here to provide an extension point for rendering cell content
						let value;
						// The summary values are inconsistent with the rest values
						// A better way is to fix the root cause and make things consistent..
						// but for now, we adjust things here to make the value format consistent before we call the extension
						if (dataValue.isSummary){
							value = {};
							if (typeof(dataValue.value) === 'object') {
								value.label = dataValue.value.label;
								value.value = dataValue.value.value;
							}else{
								value.label = dataValue.value;
								value.value = dataValue.rawValue || dataValue.value;
							}
						} else {
							value = dataValue.value;
						}
						const isRank = this.isRank(colIndex);
						const dataItemIndex = this.isRank(colIndex) ? this.getRefIndex(colIndex) + this.getRankDataItemCount() : colIndex;
						const state = {
							dataItem: this._columnDataItems[dataItemIndex],
							dataValue: value,
							rowIndex: rowIndex,
							colIndex: colIndex
						};
						if ( isRank ){
							state.isRank = true;
						}
						if ( dataValue.isSummary ) {
							state.isSummary = true;
						}
						children = this.cellRenderer.getComponentList(state);

					} else {
						children = [];
					}

					if (children.length === 0) {
						children.push(<GridContentRenderer {...contentSpec} key={colIndex}/>);
					}

					if (isSummary) {
						gridCellProps.area = 'summary';
						this._setSummaryColor(gridCellProps);
					}  else {
						children.push(React.createElement('div', { class: 'grid-drop-zone left' }, ''));
						children.push(React.createElement('div', { class: 'grid-drop-zone replace' }, ''));
						children.push(React.createElement('div', { class: 'grid-drop-zone right' }, ''));
					}

					if (currentRow + 1 >= _dataProvider.getNumRows() || isSummary) {
						gridCellProps.style.height = parseFloat(gridCellProps.style.height) - 2 + 'px';
					}
					if (currentCol + 1 >= _dataProvider.getNumColumns()) {
						gridCellProps.style.width -= 2;
					}
					if (currentCol === 0) {
						classList.push('table-first-column');
					}
					gridCellProps.style.width += 'px';

					return React.createElement(
						GridDropTarget,
						{ dndManager: this._gridDnDManager, cellType: 'column', ...gridCellProps },
						children
					);
				} catch(err) {
					// livewidget_cleanup
					// An error might occure because this function seems to access destroyed objects.
					// This happens when the grid is being re-rendered, and  for some reason this function
					// gets called before the re-render and since it references destroyed obbect (i.e. deleted dataitems)
					// we will get an exception
					// No need to do anything and the re-render will occur and things should be fine
					// But we need to understand more why this is happening.
				}

			};

			const fixedRowCellRenderer = (rowIndex, colIndex, gridCellProps) => {
				const dataValue = _dataProvider.getValue(rowIndex, colIndex);
				const isSummary = this._getDataCellValue(dataValue, 'isSummary');
				const dataCellValue = this._getDataCellValue(dataValue);

				//populate the attributes we needed
				gridCellProps['row'] = rowIndex;
				gridCellProps['col'] = colIndex;
				gridCellProps['scope'] = 'col';
				gridCellProps['aria-label'] = dataCellValue;
				gridCellProps['tabindex'] = TABINDEX;

				const classList = [gridCellProps.className];
				classList.push('grid-fixed-row-cell', 'dashboard-grid-cell', 'table-view');
				if (this._selectedHeaderColumnCache[this._getCellCacheKey(rowIndex, colIndex)]) {
					classList.push('implied_selection');
				}

				gridCellProps.style = _.extend(gridCellProps.style, {});
				//Text toolbar style applied
				gridCellProps.style = _.extend(gridCellProps.style, this.headerStyles);
				if (colIndex + 1 >= _dataProvider.getNumColumns()) {
					gridCellProps.style.width -= 2;
				}

				gridCellProps.className = classList.join(' ');

				const textCellChildren = [dataCellValue];

				//Add sorting indicator if any exists
				const sortType = this._getSortType(colIndex);
				if (sortType) {
					const sortIconID = (sortType === 'asc') ? this.iconsFeature.getIcon('caretUp').id : this.iconsFeature.getIcon('caretDown32').id;
					const sortingIndicator = <Toolkit.SVGIcon iconId={sortIconID}/>;
					textCellChildren.push(sortingIndicator);
				}

				const children = [
					React.createElement('div', { className: 'dashboard-text-cell' }, textCellChildren)
				];

				if (isSummary) {
					children.push(React.createElement('div', { className: 'grid-summary-decorator' }, ''));
				} else {
					children.push(React.createElement('div', { class: 'grid-drop-zone left' }, ''));
					children.push(React.createElement('div', { class: 'grid-drop-zone replace' }, ''));
					children.push(React.createElement('div', { class: 'grid-drop-zone right' }, ''));
				}

				return React.createElement(
					GridDropTarget,
					{ dndManager: this._gridDnDManager, cellType: 'column', ...gridCellProps },
					children
				);
			};

			const rowHeight = () => {
				return DEFAULT_ROW_HEIGHT;
			};
			const columnWidth = () => {
				const minCellWidth = 100;
				let viewPortWidth = this.domNode.clientWidth;
				if (this.domNode.clientHeight / DEFAULT_ROW_HEIGHT < numRows){
					// when vertical scrollbar is visible, it takes up the width space of current view port, which is 17px among all types of browsers and increase 1px for windows machine
					const scrollBarWidth = 18;
					viewPortWidth = viewPortWidth - scrollBarWidth;
				} else {
					// IE has around 1px differences than the other browsers
					const calWidthDiff = 1;
					viewPortWidth = viewPortWidth - calWidthDiff;
				}
				const adjustedCellWidth = parseInt(viewPortWidth / numColumns);
				return Math.max(adjustedCellWidth, minCellWidth);
			};

			const onAfterGridUpdate = () => {
				coveredCellSet = new Set();
			};

			let overscanRowCount = 2;
			if ( (this._columnDataItems ? this._columnDataItems.length : 0) > SUMMARY_COLS_THRESHOLD ) {
				// We know we won't have groups in this scenario, so its safe to overscan more rows.
				// RTC #290279 - Crosstab and Grid will sometimes show blank cells when row span is active
				overscanRowCount = 20;
			}

			const style = {};
			const props = {
				rowHeight,
				columnWidth,
				onAfterGridUpdate,
				numColumns,
				numRows,
				fixedRows,
				overscanRowCount,
				dataCellRenderer,
				fixedRowCellRenderer,
				style,
				ref: (node) => {
					this.gridRef = node;
				},
				maxColumnWidth: Infinity
			};
			const contextualGrid = React.createElement(Toolkit.Grid, props);
			this.$el = $(this.domNode).addClass('dashboard-common-grid').addClass('table-grid');
			ReactDOM.render(contextualGrid, this.domNode);
			this.gridRef && this.gridRef.recomputeGridSize();
		}

		/**
		 * @description Tell if the cell below current cell is a row-summary cell
		 */
		_isCellBelowInSummaryRow(nextRow, nextCol, dataProvider) {
			let result;
			if(dataProvider.getNumRows() > nextRow){
				const dataValue = dataProvider.getValue(nextRow, nextCol);
				result = this._getDataCellValue(dataValue, 'isSummary');
			}else{
				result = false;
			}
			return result;
		}

		_getMaxColHeight() {
			return this.domNode.offsetHeight || Infinity;
		}

		/**
		 * Callback for handling when data dynamically changes from the pivot
		 * @param {number} row - row index of the pivot data
		 * @param {number} column - column index of the pivot data
		 * @param {string} value - formatted value of the changed data
		 */
		_onCellDataChange(row, column, value, rawValue) {
			if (!this._dataRowsIsReady) {
				this._delayedCellUpdate.push({ row, column, value, rawValue });
			}
			// When the value is error, add warning icon only once
			if (value && value.error) {
				if (!this._errorWarningAdded) {
					this.gridView.onSummaryDataCellError();
					this._errorWarningAdded = true;
				}
				value = StringResources.get('value_is_not_available');
			}

			if (this._dataRows &&
				row < this._dataRows.length &&
				column < this._dataRows[row].length &&
				this._dataRows[row][column].value !== value) {
				this._dataRows[row][column].value = value;
				this._dataRows[row][column].rawValue = rawValue;
			}
		}

		/**
		 * For scenario that summaryValue is ready before dataRows are ready, we need to delay the cell update once the dataRows are resolved
		 */
		_WhenDataRowsAreReady() {
			this._dataRowsIsReady = true;
			this._delayedCellUpdate.forEach((cell) => this._onCellDataChange(cell.row, cell.column, cell.value, cell.rawValue));
		}

		/**
		 * Reset the dataRowsIsReady flag and delayedCellUpdates when dataRows are regenerated
		 */
		_resetOnGetDataRows() {
			this._dataRowsIsReady = false;
			this._delayedCellUpdate = [];
		}

		/**
		 *
		 * @param {*} value could be an object, an array or a string
		 * @param {String} type either displayValue or useValue
		 */
		_getValue(value, type) {
			if (value === null || value === undefined) {
				return;
			}

			// Since the query result value is also in the form of {value: 'aaa', label: 'AAA'}
			// Need to differentiate query result value vs. internal generated value from EdgeIterator
			if (value.value !== undefined && value.descendant_or_self !== undefined) {
				value = value.value;
			}

			if (value !== null && value !== undefined) {
				if (value[type] !== undefined) {
					// Get the either display or use value
					return value[type];
				}

				// When value has a nested value object
				if (value.value && typeof value.value === 'object') {
					return this._getValue(value.value, type);
				}

				// If label is missing, return use value
				if (type === LABEL && value.value !== undefined) {
					return value.value;
				}
			}

			// When value is an array
			if (_.isArray(value) && value[0]) {
				return this._getValue(value[0], type);
			}

			return value;
		}

		setValue($td, value, rowIndex, colIndex) {
			let cellValue = this._getValue(value, LABEL);
			const bIsSummary = (typeof value === 'object' && 'isSummary' in value) ? value.isSummary : this._dataRows[rowIndex][colIndex].isSummary;
			//Fixup the label for summary row so it gets rendered properly
			if (bIsSummary) {
				if (this._getValue(value, VALUE) === value.summaryOf) {
					cellValue = SUMMARY_ID;
				}
			} else if (rowIndex !== ROW_HEADER_INDEX) {
				//Summary values are already formatted so only format non-summary values
				cellValue = this._formatValue(cellValue, colIndex, bIsSummary);
			}

			$td.attr('aria-label', cellValue);
			$td.text(cellValue);
		}

		_getSortType(colIndex) {
			// Find the correct slot and index of data item in it
			const {
				slot,
				mapIndex
			} = this.findSlotDetails(colIndex);


			// If not sorted by rank hide sort icon for rank column, if sorted by rank hide sort icon for the non-rank column
			const dataItem = this._queryResult.getSlotDataItemList(slot)[mapIndex];
			const dataItemSort = dataItem && dataItem.getSort();
			if (dataItemSort) {
				if ((this.isRank(colIndex) && !dataItemSort.rankSort) || (!this.isRank(colIndex) && dataItemSort.rankSort)) {
					return null;
				}
			}
			return dataItemSort && !dataItem.hasDefaultSort() &&dataItemSort.type;
		}

		// Rank columns should always be formatted as number
		_getRankFormat() {
			return {
				formatSpec: {
					'selectedFormat': 'none',
					'maximumFractionDigits': 0,
					'formatLength': 'short',
					'type': 'number',
					'locale': this._columnSlot.locale
				},
				validFormatSpec: {
					'selectedFormat': 'none',
					'maximumFractionDigits': 0,
					'formatLength': 'short',
					'type': 'number',
					'locale': this._columnSlot.locale
				}
			};
		}

		_formatValue(cellValue, colIndex, bIsSummary, isSuppressed) {
			let format;
			if (this.isRank(colIndex)) {
				format = this._rankFormat;
			} else {
				format = this._slotFormatCache[colIndex];
			}

			if (!bIsSummary) {
				return isSuppressed ? Formatter.format(cellValue, format) : Formatter.formatNull(cellValue, format);
			} else {
				// only format the summary row for number
				const dataItem = this._columnDataItems[colIndex];
				if (dataItem && this._isFactOrAggregationSet(dataItem)) {
					return isSuppressed ? Formatter.format(cellValue, format) : Formatter.formatNull(cellValue, format);
				}
				return cellValue;
			}
		}

		_isSuppressionOn(){
			const content = this.gridView && this.gridView.content;
			const suppressionValue = content && content.getPropertyValue(PROP_ID_SUPPRESSION);
			if (suppressionValue && suppressionValue === 'none') {
				return false;
			}
			return true;
		}

		/**
		 * Callback for handling when data dynamically changes from the pivot
		 *
		 * @param {number} iRow - row index of the pivot data
		 * @param {number} iCol - column index of the pivot data
		 * @param {number} dataValue - value of the data
		 * @param {Array} heatColumnPosition - an array of the column position against the heat slot
		 */
		_getConditionalColor(iRow, iCol, dataValue, heatColumnPosition) {
			/*
			 * A grid can be colored if there is a heat slot populated. _heatIndice contains the index of the heat column that is displayed in
			 * the grid, which causes only that column to be colored. If the first value is -1, that means the heat slot is populated but the column
			 * is not displayed in the grid; thus, all columns are colored.
			 */

			// If this is a rank column, look at the referenced table column
			const colIndex = this.isRank(iCol) ? this.getRefIndex(iCol) + this.getRankDataItemCount() : iCol;
			if (this._heatByIndex !== -1 && heatColumnPosition.length) {
				const heatOffset = heatColumnPosition.indexOf(colIndex);
				if (heatOffset !== -1) {
					const condValue = this._queryResult.getValue(iRow, this._heatByIndex + heatOffset);
					const dataItem = this._columnDataItems[colIndex];
					return this.conditionalFormatting.getFormatting( (dataValue.value && dataValue.value.value || dataValue.value), condValue.value, dataItem.getColumnId());
				}
			}
			return null;
		}

		/**
		 * Gets a list of the nodes that have been selected
		 * @return {Array.node} - an array of nodes
		 */
		getSelectedColumnHeaderNodes() {
			const nodes = $(this.domNode).find('.header-cell.implied_selection');
			const selectedNodes = [];
			_.each(nodes, node => {
				const $node = $(node);
				// Remove all nodes under the Grid's visually-hidden node
				const ancestor = $node.closest('.visually-hidden').get();
				if (ancestor.length === 0) {
					selectedNodes.push(node);
				}
			});
			return selectedNodes;
		}

		/**
		 * highlight the column header
		 */
		hilightColumnHeaderColumn(selectedNode, bSingleSelect, bDeselect, bRightClick) {
			const $node = $(selectedNode);
			if (bDeselect && bRightClick) {
				return;
			}

			if (bSingleSelect) {
				this.clearColumnHighlight();
			}

			const rowIndex = $node.attr('row');
			const colIndex = $node.attr('col');
			const cacheKey = this._getCellCacheKey(rowIndex, colIndex);
			this._selectedHeaderColumnCache[cacheKey] = !bDeselect;

			if (bDeselect) {
				$node.removeClass('implied_selection');
			} else {
				$node.addClass('implied_selection');
			}
		}

		clearColumnHighlight() {
			$(this.domNode).find('.header-cell.implied_selection').removeClass('implied_selection');
			this._selectedHeaderColumnCache = {};
		}

		/**
		 * @returns true if the current node includes the 'selected' or 'selected_secondary' or 'implied_selection' class.
		 */
		isCellSelected(node) {
			const $node = $(node);
			return $node.hasClass('dashboard-grid-cell-selected') || $node.hasClass('implied_selection');
		}

		isGroupedColumn(columnNodeOrColumnIndex) {
			let columnIndex = columnNodeOrColumnIndex;
			if (typeof columnNodeOrColumnIndex === 'object') {
				columnIndex = this.getColumnIndex(columnNodeOrColumnIndex);
			}

			return this._columnsInfo.groupedIndices && (this._columnsInfo.groupedIndices.indexOf(parseInt(columnIndex)) !== -1);
		}

		_isFactColumn(columnIndex) {
			return this._columnsInfo.factIndices.indexOf(parseInt(columnIndex)) !== -1;
		}

		getRowIndexFromSelectedNode(selectedNode) {
			return parseInt($(selectedNode).attr('row'), 10);
		}

		getColumnIndex(cellNode) {
			return parseInt($(cellNode).attr('col'), 10);
		}

		_hasDataMappings() {
			return this.visualization.getSlots().getMappedSlotList().length > 0;
		}

		getColumnCount() {
			return this._columnDataItems.length;
		}

		/**
		 * Find the slot, index of slot in JQGrid._slots and item's mapIndex in that slot
		 * @param colIndex Column index in the table
		 */
		findSlotDetails(colIndex) {
			const realColumnIndex = this.getRealColumnIndex(colIndex);
			return this.findSlotDetailsByRealColumnIndex(realColumnIndex);
		}

		/**
		 * The rank value can depend on a value which exists in either column slot or heat slot
		 * When we need to fetch information from the slot, we should detect which slot we should use.
		 * Function returns the correct slot, its index in JQGrid._slots and data item's mapIndex in the slot.
		 *
		 * @param columnIndex Data item index in the original data
		 */
		findSlotDetailsByRealColumnIndex(columnIndex) {
			let prevItemCount = 0;
			let slot = null;
			let mapIndex = null; // index of item in slot
			let slotIndex = 0; // index of the slot

			for (let i = 0; i < this._slots.length; i++) {
				slot = this._slots[i];
				const slotItemCount = this._queryResult.getSlotDataItemList(slot).length;
				if (columnIndex < (slotItemCount + prevItemCount)) {
					mapIndex = columnIndex - prevItemCount;
					slotIndex = i;
					break;
				} else {
					prevItemCount += slotItemCount;
				}
			}

			return { slot, slotIndex, mapIndex };
		}

		/**
		 * Returns the real index of data item in the original query result data
		 * given the column's index on the table.
		 *
		 * @param {*} colIndex column index on the table
		 */
		getRealColumnIndex(colIndex) {
			return this.isRank(colIndex) ? this.getRefIndex(colIndex) : colIndex - this.getRankDataItemCount();
		}

		getRankDataItemCount() {
			return (this._rankDataItems && this._rankDataItems.length) || 0;
		}

		/**
		 * Determines whether the given column mapping is a rank column
		 * @param columIndex:	index of the column in the table
		 */
		isRank(columIndex) {
			return !!(this._columnDataItems[columIndex] && this._columnDataItems[columIndex].isRank) || false;
		}

		/**
		 * Returns the 'original' index of a data column that is being referenced by a rank column.
		 * This is the index of the referenced column BEFORE the Rank Processor modified the data.
		 * In order to get to the visible (rendered) column index, we should add the _rankDataItems.length to this.
		 *
		 * @param colIndex index of the rank
		 */
		getRefIndex(colIndex) {
			return this._columnDataItems[colIndex].getRefColumnIndex && this._columnDataItems[colIndex].getRefColumnIndex();
		}

		remove() {
			this.gridView = null;
			this.tabNavigation = null;
			this._rankDataItems = null;
			this._dataRows = null;
			this._headers = null;
			this._dataRowsIsReady = null;
			this._delayedCellUpdate = null;

			this.removeGridView();
			this.domNode = null;
		}

		removeGridView() {
			if (this.gridRef && this.domNode) {
				ReactDOM.unmountComponentAtNode(this.domNode);
				this.gridRef = null;
			}
		}

		onEnterContainer() {
			const firstCellNode = $(this.domNode).find('.top-right-grid-cell[row="0"][col="0"]');
			if (firstCellNode.length) {
				firstCellNode.focus();
			}
		}

		handleSelectColumnHeader(selectedNode, bSingleSelect, bRightClick) {
			//Existing column header will still be highlighted if the ctrl key is held
			const bDeselect = this.isCellSelected(selectedNode);
			this.hilightColumnHeaderColumn(selectedNode, bSingleSelect, bDeselect, bRightClick);
		}

		_isFactOrAggregationSet(dataItem) {
			if (!dataItem) {
				return false;
			}
			if (dataItem.getType() === 'fact') {
				return true;
			} else {
				if (dataItem.getAggregation() !== MetadataColumn.AGGREGATION_TYPE_MAP.none) {
					return true;
				}
				return false;
			}
		}

		showDropZone(selector) {
			this.hideDropZones();
			this.$el.find(selector).css({ 'display': 'block' });
		}

		hideDropZones() {
			this.$el.find('.grid-drop-zone').css({ 'display': 'none' });
		}

		showReplaceDropZone(idx, type='col') {
			this.showDropZone(`[${type}=${idx}] > .grid-drop-zone`);
		}

		showLeftDropZone(col) {
			this.showDropZone(`[col=${col}] > .grid-drop-zone.left`);
		}

		showRightDropZone(col) {
			this.showDropZone(`[col=${col}] > .grid-drop-zone.right`);
		}

		/**
		 * Determine the column position relative to the heat slot
		 */
		_determineHeatColumnPosition() {
			let heatPos = [];
			const condFormats = this.conditionalFormatting && this.conditionalFormatting.getConditionalFormatting();
			if (condFormats) {
				this._heatSlot.getDataItemList().forEach(dataItem => {
					const condFormat = condFormats.conditionalFormats.find(cf => cf.heatId === dataItem.getId());
					if (condFormat) {
						heatPos.push(this._columnsInfo.factIndices.find(factIndex => {
							return !this.isRank(factIndex) && condFormat.dataItemId === this._columnDataItems[factIndex].getColumnId();
						}));
					}
				});
			}
			return heatPos;
		}
	};
});
